"use client";

import { useCartStore } from "@/lib/store/cartStore";
import { useDictionaryStore } from "@/lib/store/dictionaryStore";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

export default function CurrencySelector() {
  const [isOpen, setIsOpen] = useState(false);
  const { setCurrency, currency } = useCartStore();
  const { exchangeRate } = useDictionaryStore();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    // Agregamos "relative" al contenedor padre para que el hijo absoluto se posicione respecto a este
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Gatillo del selector */}
      <button
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Seleccionar divisa. Moneda actual: ${currency}`}
        className="cursor-pointer gap-1 min-h-[35px] max-h-[35px] w-[50px] md:w-[100px] flex items-center justify-center text-secondary rounded-lg border border-slate-200 transition-all duration-300 group bg-transparent focus:outline-none focus:ring-2 focus:ring-primary/40"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        <Image
          src={currency === "MXN" ? "/mex-flag.webp" : "/usa-flag.webp"}
          alt={currency === "MXN" ? "Bandera de México" : "Bandera de Estados Unidos"}
          className="w-5 h-5 object-cover rounded-full"
          width={50}
          height={50}
        />
        <span className="font-bold hidden md:block">{currency}</span>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-[280px] max-w-[calc(100vw-1.5rem)] bg-white text-black rounded-lg shadow-2xl border border-white/10 z-[100] overflow-hidden">
          <div className="px-4 py-3 border-b">
            <p className="text-xs text-black/60">Tasa de cambio actual</p>
            <p className="text-sm font-bold">{exchangeRate?.toFixed(2)}</p>
          </div>
          <div className="px-4 pt-3 pb-2">
            <p className="text-xs font-semibold text-black/50 uppercase tracking-wider">
              Seleccionar Moneda
            </p>
          </div>

          {/* Opciones */}
          <div className="py-1">
            <button
              onClick={() => {
                setCurrency("MXN");
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-white/10 transition-colors ${
                currency === "MXN" ? "bg-white/10" : ""
              }`}
            >
              <Image
                src="/mex-flag.webp"
                alt="MXN"
                className="w-5 h-5 rounded-full"
                width={20}
                height={20}
              />
              <span className="font-medium flex-1 text-left">
                Peso Mexicano (MXN)
              </span>
              {currency === "MXN" && <CheckIcon />}
            </button>

            <button
              onClick={() => {
                setCurrency("USD");
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-white/10 transition-colors ${
                currency === "USD" ? "bg-white/10" : ""
              }`}
            >
              <Image
                src="/usa-flag.webp"
                alt="USD"
                className="w-5 h-5 rounded-full"
                width={20}
                height={20}
              />
              <span className="font-medium flex-1 text-left">Dólar (USD)</span>
              {currency === "USD" && <CheckIcon />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      className="w-5 h-5 text-green-400"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}
